import React, { useEffect, useState } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Ansprechpartner } from "@/components/site/Ansprechpartner";
import { Kontakt } from "@/components/site/Kontakt";
import { FlowLines } from "@/components/site/FlowLines";
import { Container, Label } from "@/components/site/Primitives";
import { Toaster } from "@/components/ui/sonner";
import ratgeber from "@/data/ratgeber.json";

export const KATEGORIEN = [
  "Alle",
  "Batteriespeicher",
  "Photovoltaik",
  "Netzanschluss",
  "Investoren",
  "Personal & Recruiting",
];

export default function RatgeberIndex() {
  const [kategorie, setKategorie] = useState("Alle");

  useEffect(() => {
    document.title = "Ratgeber: Photovoltaik, Batteriespeicher & Projektentwicklung | energyleads";
    const desc = document.querySelector('meta[name="description"]');
    if (desc)
      desc.setAttribute(
        "content",
        "Der energyleads-Ratgeber: Praxiswissen zu Batteriespeicher-Leads, Dachverpachtung, Netzanfragen, IAB-Investoren, Vertrieb und Personal für erneuerbare Energien.",
      );
    window.scrollTo(0, 0);
  }, []);

  const artikel = Object.entries(ratgeber).filter(([, a]) => kategorie === "Alle" || a.category === kategorie);

  return (
    <>
      <Header />
      <main data-testid="ratgeber-main">
        <section className="has-lines relative overflow-hidden bg-[#1e2532] pb-16 pt-[150px] text-white on-dark">
          <FlowLines />
          <Container>
            <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1.5 text-[13px] text-white/50">
              <a href="/" className="hover:text-white">Startseite</a>
              <ChevronRight size={13} />
              <span className="text-white/80">Ratgeber</span>
            </nav>
            <Label>Ratgeber</Label>
            <h1 className="mt-7 max-w-[760px] font-bold" data-testid="ratgeber-h1">
              Praxiswissen für <span style={{ color: "var(--accent)" }}>Projektentwickler</span>
            </h1>
            <p className="mt-6 max-w-[620px] text-[16.5px] leading-[1.72] text-white/72">
              Leitfäden und Checklisten aus unserer eigenen Projektentwicklung — zu Flächenakquise, Netzanfragen,
              Investoren, Vertrieb und Personal.
            </p>
          </Container>
        </section>

        <section className="section-pad has-lines overflow-hidden bg-[#f5f6f4]">
          <FlowLines color="#0F9D6E" opacity={[0.055, 0.045]} />
          <Container>
            <div className="flex flex-wrap gap-2.5" data-testid="ratgeber-filter">
              {KATEGORIEN.map((k) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`ratgeber-filter-${k}`}
                  onClick={() => setKategorie(k)}
                  aria-pressed={kategorie === k}
                  className={`rounded-full border px-4 py-2.5 text-[14px] font-medium ${
                    kategorie === k
                      ? "border-[#0f9d6e] bg-[#0f9d6e]/10 text-[#0b7d57]"
                      : "border-[#d4d8d5] bg-white text-[#565f70] hover:border-[#9aa1ad]"
                  }`}
                  style={{ transition: "border-color .3s ease, background-color .3s ease, color .3s ease" }}
                >
                  {k}
                </button>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-7">
              {artikel.map(([slug, a], i) => (
                <a
                  key={slug}
                  href={`/ratgeber/${slug}/`}
                  data-testid={`ratgeber-card-${i}`}
                  className="card-hover flex h-full flex-col overflow-hidden rounded-[16px] border border-[#dfe2e0] bg-white"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={a.image} alt={a.title} loading="eager" decoding="async" className="h-full w-full object-cover" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <div className="flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.12em] text-[#0b7d57]">
                      <span>{a.category}</span>
                      <span className="text-[#c6cbc8]">·</span>
                      <span className="font-medium normal-case tracking-normal text-[#8a90a0]">{a.readTime} Lesezeit</span>
                    </div>
                    <h2 lang="de" className="mt-4 break-words text-[19px] font-semibold leading-[1.35] text-[#1e2532]" style={{ hyphens: "auto" }}>
                      {a.title}
                    </h2>
                    <p className="mt-3 text-[15px] leading-[1.7] text-[#565f70]">{a.excerpt}</p>
                    <span className="mt-auto inline-flex items-center gap-2 pt-5 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
                      Artikel lesen <ArrowRight size={15} />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </Container>
        </section>

        <Ansprechpartner />
        <Kontakt />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}
